import { difference } from "lodash";

const REGEX_LITERAL = /^\/(.*)\/([dgimsuy]*)$/;

const normalizeStringValue = (value) => (value ?? "").trim();

const getRegex = (pattern, flags) => {
	if(!pattern) {
		return undefined;
	}

	if(pattern instanceof RegExp) {
		return new RegExp(pattern.source, flags ?? pattern.flags.replace("g", ""));
	}

	const literalMatch = pattern.match(REGEX_LITERAL);
	if(literalMatch) {
		return new RegExp(literalMatch[1], flags ?? literalMatch[2].replace("g", ""));
	}

	return new RegExp(pattern, flags);
};

const buildNewEntryExpression = (newEntryRegex) => {
	const newEntryExpression = getRegex(newEntryRegex);

	return newEntryExpression ? new RegExp(newEntryExpression.source, "g") : undefined;
};

const splitByExpression = (value, expression) => {
	if(!expression) {
		return [];
	}

	const values = [];
	const textValue = value ?? "";
	let startIndex = 0;
	let match = expression.exec(textValue);

	while(match) {
		values.push(textValue.slice(startIndex, match.index));
		startIndex = match.index + match[0].length;

		if(match[0].length === 0) {
			break;
		}

		match = expression.exec(textValue);
	}

	values.push(textValue.slice(startIndex));
	return values;
};

export const splitNewEntryValue = (value, newEntryRegexParam) => {
	const newEntryRegex = buildNewEntryExpression(newEntryRegexParam);
	const textValue = value ?? "";
	const hasNewEntry = !!newEntryRegex && newEntryRegex.test(textValue);

	if(newEntryRegex) {
		newEntryRegex.lastIndex = 0;
	}

	return {
		hasNewEntry,
		values: hasNewEntry
			? splitByExpression(textValue, newEntryRegex)
				.map(normalizeStringValue)
				.filter(valuePart => valuePart.length > 0)
			: []
	};
};

export const toStringFieldValues = (fieldId, values) => {
	return normalizeStringValues(values)
		.map((value, ordinal) => ({
			FieldId: fieldId,
			Ordinal: ordinal,
			String: value
		}));
};

export const getStoredStringValues = (fieldId, values) => {
	return normalizeStringValues(
		sortFieldValuesByOrdinal(
			values.filter(value => value.FieldId === fieldId && value.String !== undefined && value.String !== null)
		)
			.map(value => value.String)
	);
};

export const beginEditingStringValue = (values, index) => {
	const normalizedValue = normalizeStringValue(values[index]);

	return {
		items: values.filter((_, itemIndex) => itemIndex !== index),
		value: normalizedValue,
		editingIndex: index
	};
};

export const insertStringValuesAt = (values, newValues, index = values.length) => {
	const insertionIndex = Math.min(Math.max(index, 0), values.length);

	return [
		...values.slice(0, insertionIndex),
		...newValues,
		...values.slice(insertionIndex)
	];
};

export const areStringValuesEqual = (currentValues, initialValues) => {
	const areStringsSameLength = currentValues.length === initialValues.length;

	if(!areStringsSameLength) {
		return false;
	}

	const doStringsMatch = difference(currentValues, initialValues);

	return doStringsMatch.length === 0;
};

export function normalizeStringValues(values) {
	return values
		.map(normalizeStringValue)
		.filter(value => value.length > 0);
}

export const isCharacterAllowed = (character, allowedCharacterRegex) => {
	const allowedExpression = getRegex(allowedCharacterRegex);

	return !allowedExpression || allowedExpression.test(character);
};

export const isNewEntryCharacter = (character, newEntryRegex) => {
	const newEntryExpression = buildNewEntryExpression(newEntryRegex);

	return !!newEntryExpression && newEntryExpression.test(character);
};

export const isValueAllowed = (value, { allowedCharacterRegex, maxValueLength } = {}) => {
	const normalizedValue = normalizeStringValue(value);

	if(normalizedValue.length === 0) {
		return false;
	}

	if(maxValueLength !== undefined && normalizedValue.length > maxValueLength) {
		return false;
	}

	const allowedExpression = getRegex(allowedCharacterRegex);
	if(!allowedExpression) {
		return true;
	}

	const fullAllowedRegex = new RegExp(`^(?:${allowedExpression.source})+$`, allowedExpression.flags);
	
	return fullAllowedRegex.test(normalizedValue);
};

export const getAllowedStringValues = (values, options = {}) => {
	return normalizeStringValues(values)
		.filter(value => isValueAllowed(value, options));
};

export const limitStringValues = (values, maxValues) => {
	if(maxValues === undefined) {
		return values;
	}

	return maxValues <= 0 ? [] : values.slice(0, maxValues);
};

export function sortFieldValuesByOrdinal(fieldValues) {
	return [...fieldValues].sort((leftValue, rightValue) => (leftValue.Ordinal ?? 0) - (rightValue.Ordinal ?? 0));
}
